import React, { useEffect, useCallback, useRef, useState } from 'react';
import 'moment/locale/da.js';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import WinterDutyCalendarLine from './WinterDutyCalendarLine';
import './WinterDutyCalendar.css';
import { useSelector, useDispatch } from 'react-redux';
import { setStartThunk, setHighLight, setNumberOfDaysThunk } from '../../store/CalendarStore';
import moment from 'moment';
import { Day } from '../planning/Day';
import RouteLegend from './RouteLegend';

const WinterDutyCalendar = () => {
    const dispatch = useDispatch();
    const [selectedDuty, setSelectedDuty] = useState(null);

    const calendarState = useSelector((state) => state.calendarState);
    const winterDutyState = useSelector((state) => state.winterDutyState);
    const numberOfDays = calendarState.numberOfDays;
    
    if (numberOfDays == undefined) {
        console.error('numberOfDays is undefined in WinterDutyCalendar.js');
        return null;        
    }

    const headerRef = useRef(null);
    const bodyRef = useRef(null);

    useEffect(() => {
        dispatch(setStartThunk(moment().startOf('day').valueOf(), true));
        if (bodyRef.current && headerRef.current) {
            const scrollbarWidth = bodyRef.current.offsetWidth - bodyRef.current.clientWidth;
            headerRef.current.style.marginRight = `${scrollbarWidth}px`;
        }
    }, [dispatch]);

    // Memoized functions for dispatch logic
    const handleSetStart = useCallback(() => {
        dispatch(setStartThunk(moment().startOf('day').valueOf(), true));
    }, [dispatch]);

    const handleDateChange = useCallback((date) => {
        dispatch(setStartThunk(new Date(date[0]).valueOf(), true));
    }, [dispatch]);

    const handleChangeNumberOfDays = useCallback((nod) => {
        dispatch(setNumberOfDaysThunk(nod, true));
    }, [dispatch]);

    const handleHighlight = useCallback((id) => {
        dispatch(setHighLight(id));
    }, [dispatch]);

    const handleDutyClick = useCallback((duty, event) => {
        setSelectedDuty(duty);
    }, []);

    const handleDayClick = useCallback((day) => {
        dispatch(setStartThunk(day, true));
    }, [dispatch]);

    const winterDuties = winterDutyState.winterDuties;

    // Group winter duties by person
    const pWinterDuties = [];
    winterDuties.forEach(x => {
        if(!pWinterDuties[x.personId]) pWinterDuties[x.personId] = [];
        pWinterDuties[x.personId].push(x);
    });

    // Build hours array for hourly view (supports multi-day hourly when numberOfDays <= 3)
    let hours = [];
    const hoursCount = 24 * Math.max(1, numberOfDays);
    for (let i = 0; i < hoursCount; i++) {
        hours.push(moment(calendarState.days[0]).startOf('day').add(i, 'hours'));
    }

    const createdPeople = {};
    const createUniqueKey = (p) => {
        let key = p.id;

        if(createdPeople.hasOwnProperty(p.id)){
            key = `${key}-${createdPeople[p.id]}`;
            createdPeople[p.id] = createdPeople[p.id] + 1;
        }else {
            createdPeople[p.id] = 1;
        }
        return key;
    }

    const renderLine = (p, i) => {
        const key = createUniqueKey(p);    
        // Use hourly view for 1 to 3 days; date (day boxes) for >3 days
        const dateView = numberOfDays > 3;

        if(p.hidden){
            return;
        }
        return(
            <WinterDutyCalendarLine
                key={key}
                person={p}
                winterDuties={pWinterDuties[p.id] || []}
                days={dateView ? calendarState.days.map((day) => moment(day)) : hours}
                dayWidth={dateView ? 100 / numberOfDays : 100 / (24 * Math.max(1, numberOfDays))}
                granularity={dateView ? 'date' : 'hour'}
                highLightPersonId={calendarState.highLightPersonId}
                onClick={() => handleHighlight(p.id)}
                onDutyClick={handleDutyClick}
                startTime={calendarState.days[0]}
                totalTime={numberOfDays}
            />
        )
    };

    const renderWeeks = (days) => {
        let arr = [];
        let lastWeek = null;
        let pointer = -1;

        days.forEach(d => {
            const curWeek = moment(d).week();
            if(curWeek === lastWeek){
                arr[pointer].count++;
            }
            else{
                pointer++;
                arr[pointer] = {week: curWeek, count: 1};
            }
            lastWeek = curWeek;
        });

        return arr.map((e) => {
            const style = { width: `${calendarState.dayWidth * e.count}%`};
            return <div className="dd-week" key={`${e.week}-${e.count}`} style={style}>UGE {e.week}</div>;
        } );
    };

    const renderMonth = (entry) => {
        const monthNames = ['Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'December'];
        const style = { width: `${calendarState.dayWidth * entry.count}%`};
        return(
            <div className="dd-month" key={`${entry.month}-${entry.count}`} style={style}>{monthNames[entry.month]}</div>
        );
    };

    const renderMonths = (days) => {
        let arr = [];
        let lastMonth = null;
        let pointer = -1;

        days.forEach(d => {
            const curMonth = moment(d).month();
            if(curMonth === lastMonth){
                arr[pointer].count++;
            }
            else{
                pointer++;
                arr[pointer] = {month: curMonth, count: 1};
            }
            lastMonth = curMonth;
        });

        return arr.map((e) => renderMonth(e));
    };

    const renderHours = () => {
        const totalHours = 24 * Math.max(1, numberOfDays);
        const width = 90 / totalHours;
        return Array.from({ length: totalHours }).map((_, idx) => {
            const style = { width: `${width}%` };
            const hour = idx % 24;
            const padded = hour.toString().padStart(2, '0');
            const label = numberOfDays === 3 ? padded : `${padded}:00`;
            return <div className="dd-hour" key={`h-${idx}`} style={style}>{label}</div>;
        });
    };

    const personMap = () => {
        const calendarStart = new Date(calendarState.days[0]);
        const calendarEnd = new Date(calendarStart.getTime() + (numberOfDays * 24 * 60 * 60 * 1000));
        
        const uPersons = {};
        winterDuties.forEach(duty => {
            const dutyStart = new Date(duty.start);
            const dutyEnd = new Date(duty.end);
            
            // Only include people who have duties that intersect with the visible calendar period
            const dutiesIntersect = dutyEnd > calendarStart && dutyStart < calendarEnd;
            
            if (duty.person && !uPersons[duty.personId] && dutiesIntersect) {
                uPersons[duty.personId] = {
                    ...duty.person,
                    hidden: false,
                    route: duty.route,
                    routeSort: duty.route?.sort ?? Infinity
                };
            }
        });

        // Group persons by route
        const routeGroups = {};
        Object.values(uPersons).forEach(person => {
            const routeId = person.route?.id || 'no-route';
            if (!routeGroups[routeId]) {
                routeGroups[routeId] = {
                    route: person.route,
                    routeSort: person.routeSort,
                    persons: []
                };
            }
            routeGroups[routeId].persons.push(person);
        });

        // Sort persons within each route by name
        Object.values(routeGroups).forEach(group => {
            group.persons.sort((a, b) => a.name.localeCompare(b.name));
        });

        // Sort route groups by route sort value
        const sortedRouteGroups = Object.values(routeGroups).sort((a, b) => {
            if (a.routeSort === Infinity && b.routeSort === Infinity) return 0;
            if (a.routeSort === Infinity) return 1;
            if (b.routeSort === Infinity) return -1;
            return a.routeSort - b.routeSort;
        });

        // Flatten the sorted groups into a single array of persons
        const sortedPersons = [];
        sortedRouteGroups.forEach(group => {
            sortedPersons.push(...group.persons);
        });

        return sortedPersons.map((p, i) => renderLine(p, i));
    };

    const calendarChooser = () => {
        return(
            <div className="calender-chooser">
                <button className={'period-pick' + (numberOfDays === 180 ? ' current' : '')} onClick={() => handleChangeNumberOfDays(180)}>6 måneder</button>
                <button className={'period-pick' + (numberOfDays === 90  ? ' current' : '')} onClick={() => handleChangeNumberOfDays(90)}>3 måneder</button>
                <button className={'period-pick' + (numberOfDays === 30  ? ' current' : '')} onClick={() => handleChangeNumberOfDays(30)}>1 måned</button>
                <button className={'period-pick' + (numberOfDays === 14  ? ' current' : '')} onClick={() => handleChangeNumberOfDays(14)}>2 uger</button>
                <button className={'period-pick' + (numberOfDays === 3  ? ' current' : '')} onClick={() => handleChangeNumberOfDays(3)}>3 dage</button>
                <button className={'period-pick' + (numberOfDays === 1  ? ' current' : '')} onClick={() => handleChangeNumberOfDays(1)}>Dagsoverblik</button>
                <div className={'period-pick'}>|</div>
                <button className={'period-pick'} onClick={handleSetStart}>Nu</button>

                <div className="datepicker-container">
                    <Flatpickr 
                        value={moment(calendarState.start).format('YYYY-MM-DD')} 
                        options={{minDate: moment().format('YYYY-MM-DD')}}
                        onChange={handleDateChange}
                        className="date-picker-custom"/>
                </div>
            </div>
        );
    };

    const dayComponents = calendarState.days.map((day, i) => 
        <Day 
            key={i} 
            size={calendarState.dayWidth} 
            day={day} 
            onClick={handleDayClick}
        />
    );
    const monthComponents = renderMonths(calendarState.days);
    const weekComponents = renderWeeks(calendarState.days);
    const lineComponents = personMap();
    // For hourly view across up to 3 days, use minutes since the current calendar start
    const minutesSinceStart = moment().diff(moment(calendarState.days[0]).startOf('day'), 'minutes');
    const totalMinutes = 1440 * Math.max(1, numberOfDays);
    const progress = Math.max(0, Math.min(1, minutesSinceStart / totalMinutes));

    return (
        <div className="content" onClick={() => setSelectedDuty(null)}>
            {calendarChooser()}
            <div className='calendar'>
                <div className="header" ref={headerRef}>
                    <div className="row">
                        <span className="header-name"/>
                        {monthComponents}
                    </div>
                    <div className="row">
                        <span className="header-name"/>
                        {weekComponents}
                    </div>
                    <div className={'row c-' + (numberOfDays)}>
                        <span className="header-name"/>
                        {dayComponents}
                    </div>
                    {numberOfDays <= 3 ?
                        <div className={'row'}>
                            <span className="header-name"/>
                            {renderHours()}
                        </div> : null
                    }
                </div>
                <div className='calendar-body' ref={bodyRef}>
                    {lineComponents}
                    {numberOfDays <= 3 && minutesSinceStart >= 0 && minutesSinceStart <= totalMinutes ? (
                        <div className='hour-line' style={{ left: `calc(${progress * 90}% + 10%)` }} />
                    ) : null}
                </div>
                {selectedDuty && (

                    <div className="info-box">
                        <div className="background"></div>
                        <div className="text-wrapper">
                            <span>
                                {selectedDuty.route?.name || 'Unknown Route'}
                                {selectedDuty.route?.description && ` - ${selectedDuty.route.description}`}
                            </span>     
                            <div>
                                {moment(selectedDuty.start).format('DD/MM/YYYY HH:mm')} til {moment(selectedDuty.end).format('DD/MM/YYYY HH:mm')}
                            </div>
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="bi bi-x-circle-fill close" viewBox="0 0 16 16" onClick={() => setSelectedDuty(null)}>
                            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" />
                        </svg>
                    </div>
                )}
            </div>
          <RouteLegend />
        </div>
    );
}

export default WinterDutyCalendar;
